<template>
  <div
    v-if="displayTranslation"
    data-testid="translation-section"
    class="rounded-lg border border-accent-two-300 bg-accent-two-100 p-4"
  >
    <div class="flex items-start justify-between mb-2">
      <h3 class="text-sm font-medium text-accent-two-800">
        Reactie op bericht in de taal van de klant
      </h3>
      <div class="flex flex-col items-end gap-1">
        <!-- Send button -->
        <div v-if="displayTranslation && !isSending" class="flex gap-2">
          <button
            class="text-xs bg-primary-500 hover:bg-primary-800 text-white px-3 py-1 rounded-md transition-colors cursor-pointer"
            data-testid="send-translation-button"
            @click="handleSendMessage"
          >
            Versturen
          </button>
        </div>
        <span
          v-else-if="isSending"
          class="text-xs text-primary-500 flex items-center gap-1"
          data-testid="sending-indicator"
        >
          <svg class="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          Verzenden...
        </span>
        <!-- Success message -->
        <span
          v-if="sendStatus === 'success'"
          class="text-xs text-accent-two-500 flex items-center gap-1"
          data-testid="success-indicator"
        >
          <svg
            class="h-3 w-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M5 13l4 4L19 7"
            ></path>
          </svg>
          Succesvol verzonden
        </span>
        <!-- Error message -->
        <span
          v-if="sendStatus === 'error'"
          class="text-xs text-accent-three-500 flex items-center gap-1"
          data-testid="error-indicator"
        >
          <svg
            class="h-3 w-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            ></path>
          </svg>
          Verzenden mislukt
        </span>
      </div>
    </div>
    <!-- Translation content -->
    <div class="space-y-2">
      <div
        class="text-accent-two-800 leading-relaxed markdown-content"
        data-testid="translation-text"
        v-html="renderedTranslation"
      ></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { GenesysWebSocketService } from '@/services/GenesysWebSocketService';
import { renderMarkdown } from '@/utils/markdown';

interface Props {
  translation?: string;
  localTranslationResult?: string;
}

const props = defineProps<Props>();

const isSending = ref(false);
const sendStatus = ref<'idle' | 'success' | 'error'>('idle');

// Computed property to determine what translation text to display
// Priority: 1. Local translation result (user-generated), 2. Original translation prop
const displayTranslation = computed(() => {
  return props.localTranslationResult || props.translation;
});

// Computed property to render markdown
const renderedTranslation = computed(() =>
  renderMarkdown(displayTranslation.value || '')
);

const handleSendMessage = async () => {
  const messageToSend = displayTranslation.value;
  if (!messageToSend?.trim()) {
    isSending.value = false;
    return;
  }

  isSending.value = true;
  sendStatus.value = 'idle';

  try {
    const success =
      await GenesysWebSocketService.sendMessageToCustomer(messageToSend);

    if (success) {
      sendStatus.value = 'success';
    } else {
      sendStatus.value = 'error';
      console.error('Failed to send message to customer');
    }
  } catch (error) {
    sendStatus.value = 'error';
    console.error('Error sending message:', error);
  } finally {
    isSending.value = false;
  }
};
</script>
